import { CarfrontCvna } from '@carvana/ds-icons';
import dayjs from 'dayjs';
import React from 'react';
import useOrderSummaryVm from '|/components/carvana-ds/order-summary-display/use-order-summary-vm';
import currency from '|/helpers/currency';
import { ReviewSummaryResponse } from '|/services/purchase-api';
import { AmountRow, ExpanderRow } from './amount-row';
import PriceSection from './price-section';
import { PriceDetailsView } from '../derive-price-details';
import { deriveVehiclePriceRows } from '../derive-vehicle-price-rows';
import styles from '../price-details.module.css';

interface Incentive {
  description: string;
  amount: number;
  expirationDate?: string | null;
}

interface VehiclePriceSectionProps {
  summary: ReviewSummaryResponse;
  view: PriceDetailsView;
}

const VehiclePriceSection = ({ summary, view }: VehiclePriceSectionProps) => {
  const {
    collapsedFees,
    setCollapsedFees,
    collapsedShipping,
    setCollapsedShipping,
    collapsedIncentives,
    setCollapsedIncentives,
    totalTaxes,
    totalFeesAmount,
    shippingFee,
    subtotal,
    msrp,
    vehicleStickerPrice,
    carvanaDiscount,
    totalDiscountsAmount,
    totalIncentives,
    incentives,
    creditKarmaAmountSaved,
    tradeBoostAmountSaved,
    employeeDiscountAmount,
    feesList
  } = useOrderSummaryVm(summary);

  const { rows, showIncentives } = deriveVehiclePriceRows({
    msrp,
    vehicleStickerPrice,
    carvanaDiscount,
    tradeBoostAmountSaved,
    creditKarmaAmountSaved,
    employeeDiscountAmount,
    priceReductions: totalDiscountsAmount,
    totalIncentives
  });

  return (
    <PriceSection
      icon={<CarfrontCvna size={16} color="var(--icon-strong)" />}
      title="Vehicle price"
      testId="pp_price-details_vehicle-price"
    >
      {rows.map(row => (
        <AmountRow
          key={row.id}
          label={row.label}
          amount={`${row.isDiscount ? '-' : ''}${currency(row.amount)}`}
          emphasis={row.isDiscount ? 'discount' : 'default'}
          testId={`pp_price-details_vehicle-price-${row.id}`}
        />
      ))}

      {showIncentives && (
        <ExpanderRow
          label="Manufacturer incentives"
          amount={`-${currency(totalIncentives ?? 0)}`}
          isOpen={!collapsedIncentives}
          onToggle={() => {
            setCollapsedIncentives(!collapsedIncentives);
          }}
          analyticsId="pp_review_order_summary_incentives_toggle"
          testId="pp_price-details_incentives-toggle"
          hideAmountWhenOpen
        >
          {(incentives as Incentive[] | null)?.map(incentive => (
            <AmountRow
              key={incentive.description}
              label={
                <>
                  {incentive.description}
                  {incentive.expirationDate && (
                    <span className={styles.rowSubtext}>
                      Valid until {dayjs(incentive.expirationDate).format('MMMM D, YYYY')}
                    </span>
                  )}
                </>
              }
              amount={`-${currency(incentive.amount)}`}
              emphasis="muted"
            />
          ))}
        </ExpanderRow>
      )}

      <div className={styles.taxGroup}>
        <AmountRow label="Sales tax" amount={currency(totalTaxes)} testId="sales-tax-row" />
        {view.showTaxSavings && (
          <p className={styles.savingsHighlight} data-testid="pp_price-details_tax-savings">
            {view.taxSavingsCopy}
          </p>
        )}
      </div>

      {view.showTitleAndRegistration && (
        <ExpanderRow
          label="Title and registration"
          amount={currency(totalFeesAmount)}
          isOpen={!collapsedFees}
          onToggle={() => {
            setCollapsedFees(!collapsedFees);
          }}
          analyticsId="pp_review_order_summary_fees_toggle"
          testId="pp_price-details_fees-toggle"
          hideAmountWhenOpen
        >
          {feesList?.map((fee: { description: string; amount: number }) => (
            <AmountRow
              key={fee.description}
              label={fee.description}
              amount={currency(fee.amount)}
              emphasis="muted"
            />
          ))}
        </ExpanderRow>
      )}

      {view.showShipping && (
        <ExpanderRow
          label="Nonrefundable shipping"
          amount={currency(shippingFee ?? 0)}
          isOpen={!collapsedShipping}
          onToggle={() => {
            setCollapsedShipping(!collapsedShipping);
          }}
          analyticsId="pp_price_details_shipping_toggle"
          testId="pp_price-details_shipping-toggle"
        >
          <p className={styles.expanderCopy}>
            Transporting the vehicle to a Carvana location near you.
          </p>
        </ExpanderRow>
      )}

      <AmountRow label="Dealer fees" amount="NEVER" />

      <AmountRow label="Subtotal" amount={currency(subtotal)} emphasis="total" />
    </PriceSection>
  );
};

export default VehiclePriceSection;
